import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { stringify } from 'qs';
import { EMPTY, map, Observable } from 'rxjs';
import { ProjectStatusEnum, ProjectStatusFilter } from '../../enums';
import { extractStrapiData, toStrapiRequestPayload } from '../../helpers';
import { ProjectEditionStatus } from '../../helpers/project-status.helper';
import { EnclosureMapper, ProjectMapper } from '../../mappers';
import {
  BatterySizingResponse,
  MaterialBom,
  PageRequest,
  Project,
  ProjectCreate,
  ProjectCreation,
  ProjectForList,
  ProjectSwitchgearRelation,
  RenderProjectPdfResponse,
  StrapiPageResponse,
  StrapiQueryParams,
  StrapiResponse,
  SvgDrawingProperties,
} from '../../models';
import {
  AvailableEnclosuresResponse,
  EnclosureFilters,
  EnclosureProposition,
} from '../../models/enclosure.model';
import {
  blocVoltageToNumber,
  SizingTool,
} from '../../models/sizing-tool.model';

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly http = inject(HttpClient);

  private readonly URL = 'api/projects';
  private readonly listQueryParams: StrapiQueryParams = {
    fields: [
      'purchaseOrder',
      'clientName',
      'projectInfo',
      'total',
      'stringQuantity',
      'quantityBatteryPerString',
      'cableSection',
      'editionStatus',
      'projectStatus',
      'isArchived',
      'createdAt',
      'selectedEnclosureType',
      'selectedEnclosureReference',
    ],
    populate: {
      planFile: {
        fields: ['url'],
      },
      client: {
        fields: ['name'],
        populate: {
          logo: {
            fields: ['url'],
          },
        },
      },
      battery: {
        fields: ['id', 'documentId', 'partNumber'],
      },
      createdByUser: {
        fields: ['username', 'firstname', 'lastname'],
      },
    },
  };
  private readonly detailQueryParams: StrapiQueryParams = {
    populate: {
      planFile: true,
      estimateFile: true,
      estimateExcelFile: true,
      planSvgFile: true,
      sizingFile: true,
      sizingTool: {
        populate: {
          batteryRanges: {
            fields: ['id', 'code', 'name', 'documentId'],
          },
        },
      },
      manualSizing: {
        populate: {
          battery: {
            fields: ['partNumber'],
          },
        },
      },
      sizingToolResults: {
        populate: {
          battery_range: {
            fields: ['id', 'documentId', 'code', 'name'],
          },
          configuration: {
            populate: {
              battery: {
                fields: ['id', 'documentId', 'partNumber'],
              },
            },
          },
        },
      },
      client: {
        fields: [
          'name',
          'middlePointType',
          'defaultSwitchgearAnchor',
          'defaultSwitchgearVerticalPlacement',
          'defaultSwitchgearTierUsagePolicy',
        ],
        populate: {
          logo: {
            fields: ['formats', 'url', 'mime'],
          },
        },
      },
      battery: {
        fields: ['partNumber'],
        populate: {
          connector: true,
        },
      },
      suppliers: {
        fields: ['id', 'documentId', 'name', 'api'],
      },
      bom: true,
      selectedSwitchgear: {
        fields: [
          'documentId',
          'reference',
          'name',
          'description',
          'currentRating',
          'width',
          'depth',
          'height',
          'reservedWidth',
          'reservedDepth',
          'requiredHeight',
          'cableShoeDiameter',
        ],
        populate: {
          schemaFile: {
            fields: ['formats', 'url', 'mime', 'width', 'height'],
          },
        },
      },
      noSupport: {
        fields: [
          'nbTiers',
          'nbRows',
          'nbBatteriesByRow',
          'distanceBetweenTiers',
        ],
      },
      deliverySlipFiles: true,
      invoiceFiles: true,
      paletteNumberFiles: true,
      commandFiles: true,
      enclosurePropositions: {
        populate: {
          propositions: {
            populate: {
              cabinet: {
                fields: [
                  'documentId',
                  'reference',
                  'internalWidth',
                  'internalDepth',
                  'intermediateHeight',
                  'externalWidth',
                  'externalDepth',
                  'externalHeight',
                ],
              },
              rack: {
                fields: [
                  'documentId',
                  'reference',
                  'internalWidth',
                  'internalDepth',
                  'intermediateHeight',
                  'externalWidth',
                  'externalDepth',
                  'externalHeight',
                ],
              },
            },
          },
        },
      },
      createdByUser: {
        fields: ['id', 'firstname', 'lastname', 'username'],
      },
    },
  };

  getAllProjects(
    pageRequest: PageRequest<Project>,
    projectStatusFilter: ProjectStatusFilter,
    filters?: {
      projectNumber?: string;
      batteryRef?: string;
      clientIds?: string[];
      clientName?: string;
      projectInfo?: string;
      installationType?: string;
      purchaseOrder?: string;
      sections?: number[] | null;
      createdAfter?: string | null;
      createdBefore?: string | null;
      createdBy?: string;
      createdByUserId?: string | null;
    },
  ) {
    const queryParams: StrapiQueryParams = JSON.parse(
      JSON.stringify(this.listQueryParams),
    );

    queryParams.pagination = {
      pageSize: pageRequest.size,
      page: pageRequest.page,
    };

    // Sort
    if (pageRequest.sort) {
      queryParams.sort = [
        `${pageRequest.sort?.property.toString()}:${pageRequest.sort?.order}`,
      ];
    }

    // Project status filter
    this.generateProjectStatusFilterQueryParams(
      queryParams,
      projectStatusFilter,
    );

    if (filters) {
      queryParams.filters = this.mergeFilters(queryParams.filters, filters);
    }

    const paramsSt = stringify(queryParams);
    return this.http.get<StrapiPageResponse<Project>>(
      `${this.URL}?${paramsSt}`,
    );
  }

  private mergeFilters(
    existingFilters: StrapiQueryParams['filters'],
    filters: {
      projectNumber?: string;
      batteryRef?: string;
      clientIds?: string[];
      clientName?: string;
      projectInfo?: string;
      installationType?: string;
      purchaseOrder?: string;
      sections?: number[] | null;
      createdAfter?: string | null;
      createdBefore?: string | null;
      createdBy?: string;
      createdByUserId?: string | null;
    },
  ) {
    const merged = existingFilters ? { ...existingFilters } : {};

    if (filters.batteryRef) {
      merged.battery = {
        ...(merged.battery ?? {}),
        partNumber: { $containsi: filters.batteryRef },
      };
    }

    if (filters.projectNumber) {
      merged.id = { $containsi: filters.projectNumber };
    }

    if (filters.purchaseOrder) {
      merged.purchaseOrder = { $containsi: filters.purchaseOrder };
    }

    if (filters.clientIds?.length) {
      const documentIds = filters.clientIds.filter(
        (value) => !/^\d+$/.test(value),
      );
      const numericIds = filters.clientIds
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value));
      const clientOrFilters = [
        documentIds.length ? { documentId: { $in: documentIds } } : undefined,
        numericIds.length ? { id: { $in: numericIds } } : undefined,
      ].filter(Boolean);

      merged.client = {
        ...(merged.client ?? {}),
        ...(clientOrFilters.length ? { $or: clientOrFilters } : {}),
      };
    }

    if (filters.sections?.length) {
      merged.cableSection = { $in: filters.sections };
    }

    if (filters.clientName) {
      merged.clientName = { $containsi: filters.clientName };
    }

    if (filters.projectInfo) {
      merged.projectInfo = { $containsi: filters.projectInfo };
    }

    if (filters.installationType) {
      const installationType = filters.installationType
        .trim()
        .replace(/\s+/g, '_');
      if (installationType) {
        merged.selectedEnclosureReference = {
          $containsi: installationType,
        };
      }
    }

    if (filters.createdAfter || filters.createdBefore) {
      merged.createdAt = {
        ...(merged.createdAt ?? {}),
      };
      if (filters.createdAfter) {
        merged.createdAt.$gte = filters.createdAfter;
      }
      if (filters.createdBefore) {
        merged.createdAt.$lte = filters.createdBefore;
      }
    }

    if (filters.createdByUserId) {
      merged.createdByUser = {
        ...(merged.createdByUser ?? {}),
        id: { $eq: filters.createdByUserId },
      };
    } else if (filters.createdBy) {
      merged.createdByUser = {
        ...(merged.createdByUser ?? {}),
        $or: [
          { username: { $containsi: filters.createdBy } },
          { firstname: { $containsi: filters.createdBy } },
          { lastname: { $containsi: filters.createdBy } },
        ],
      };
    }

    return merged;
  }

  private generateProjectStatusFilterQueryParams(
    params: StrapiQueryParams,
    projectStatusFilter: ProjectStatusFilter,
  ) {
    switch (projectStatusFilter) {
      case ProjectStatusFilter.ALL_ACTIVE_PROJECTS:
        params.filters = {
          isArchived: { $eq: false },
        };
        break;
      case ProjectStatusFilter.SIZING_COMPLETED:
        params.filters = {
          projectStatus: {
            $eq: ProjectStatusEnum.SIZING_COMPLETED,
          },
          isArchived: { $eq: false },
        };
        break;
      case ProjectStatusFilter.VALIDATED:
        params.filters = {
          projectStatus: {
            $eq: ProjectStatusEnum.PLAN_VALIDATED,
          },
          isArchived: { $eq: false },
        };
        break;
      case ProjectStatusFilter.ARCHIVED:
        params.filters = {
          isArchived: { $eq: true },
        };
        break;
    }
  }

  getProject(projectId: string): Observable<Project> {
    const paramsSt = stringify(this.detailQueryParams);
    return this.http
      .get<StrapiResponse<Project>>(`${this.URL}/${projectId}?${paramsSt}`)
      .pipe(map((projectStrapi) => extractStrapiData(projectStrapi)));
  }

  updateProject(
    projectToUpdate: Partial<ProjectCreate>,
    nextEditionStatus: ProjectEditionStatus,
    shouldClean = true,
  ): Observable<Project> {
    const url = `${this.URL}/${projectToUpdate.documentId}`;
    const project = ProjectMapper.prepareProjectCreate(
      projectToUpdate,
      shouldClean,
    );
    project.editionStatus = nextEditionStatus;
    const body = toStrapiRequestPayload(
      this.prepareProjectWritePayload(project),
    );

    return this.http
      .put<StrapiResponse<Project>>(url, body)
      .pipe(map((response) => extractStrapiData(response)));
  }

  updateBom(
    projectId: string,
    bom: MaterialBom[],
    nextEditionStatus: ProjectEditionStatus,
  ): Observable<Project> {
    const preparedBom = ProjectMapper.prepareBomsCreate(bom);
    const body = toStrapiRequestPayload({
      bom: preparedBom,
      editionStatus: nextEditionStatus,
    });

    return this.http
      .put<StrapiResponse<Project>>(`${this.URL}/${projectId}/bom`, body)
      .pipe(map((response) => extractStrapiData(response)));
  }

  private prepareProjectWritePayload(
    project: Partial<ProjectCreate>,
  ): Partial<ProjectCreate> & { selectedSwitchgear?: string | null } {
    const selectedSwitchgear = project.selectedSwitchgear;

    if (
      selectedSwitchgear &&
      typeof selectedSwitchgear === 'object' &&
      'documentId' in selectedSwitchgear &&
      typeof selectedSwitchgear.documentId === 'string'
    ) {
      return {
        ...project,
        selectedSwitchgear:
          selectedSwitchgear.documentId as ProjectSwitchgearRelation & string,
      };
    }

    return project as Partial<ProjectCreate> & {
      selectedSwitchgear?: string | null;
    };
  }

  archiveProject(
    project: Pick<ProjectForList, 'documentId' | 'editionStatus'>,
  ) {
    if (!project.documentId || !project.editionStatus) {
      return EMPTY;
    }

    return this.updateProject(
      {
        documentId: project.documentId,
        isArchived: true,
      },
      project.editionStatus,
      false,
    );
  }

  unarchiveProject(
    project: Pick<ProjectForList, 'documentId' | 'editionStatus'>,
  ) {
    if (!project.documentId || !project.editionStatus) {
      return EMPTY;
    }

    return this.updateProject(
      {
        documentId: project.documentId,
        isArchived: false,
      },
      project.editionStatus,
      false,
    );
  }

  createProject(
    projectToCreate: ProjectCreate | ProjectCreation,
  ): Observable<Project> {
    const projectStatus = ProjectStatusEnum.DRAFT;

    const body = toStrapiRequestPayload({
      ...projectToCreate,
      editionStatus: ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_CHOICE,
      projectStatus,
    });

    return this.http
      .post<StrapiResponse<Project>>(this.URL, body)
      .pipe(
        map((response: StrapiResponse<Project>) => extractStrapiData(response)),
      );
  }

  deleteProject(projectId: string) {
    const url = `${this.URL}/${projectId}`;
    return this.http.delete(url);
  }

  generateBom(projectId: string): Observable<MaterialBom[]> {
    return this.http.get<MaterialBom[]>(`${this.URL}/${projectId}/generateBom`);
  }

  generateSvgDrawing(
    projectId: string,
    forPrint: boolean,
  ): Observable<SvgDrawingProperties> {
    return this.http.get<SvgDrawingProperties>(
      `${this.URL}/${projectId}/generateSvgDrawing`,
      {
        params: { forPrint },
      },
    );
  }

  renderProjectPdf(
    projectId: string,
    options: {
      persist?: boolean;
      forPrint?: boolean;
      mode?: 'full' | 'sizing-only' | 'plan-only' | 'estimate';
      format?: 'pdf' | 'xlsx';
    },
  ): Observable<RenderProjectPdfResponse>;
  renderProjectPdf(
    projectId: string,
    options: {
      persist?: boolean;
      forPrint?: boolean;
      mode?: 'full' | 'sizing-only' | 'plan-only' | 'estimate';
      format?: 'pdf' | 'xlsx';
    } = {},
  ): Observable<RenderProjectPdfResponse> {
    const params: Record<string, string | boolean> = {};
    if (options.persist !== undefined) {
      params['persist'] = options.persist;
    }
    if (options.forPrint !== undefined) {
      params['forPrint'] = options.forPrint;
    }
    if (options.mode !== undefined) {
      params['mode'] = options.mode;
    }
    if (options.format !== undefined) {
      params['format'] = options.format;
    }

    return this.http.post<RenderProjectPdfResponse>(
      `api/pdf/project/${projectId}/render`,
      { forPrint: options.forPrint },
      { params, responseType: 'json' },
    );
  }

  /**
   * battery sizing generation
   */
  generateSizing(
    sizingTool: SizingTool,
    clientDocumentId?: string,
  ): Observable<BatterySizingResponse> {
    const body = toStrapiRequestPayload({
      ...sizingTool,
      blocVoltage: blocVoltageToNumber(sizingTool.blocVoltage),
      ...(clientDocumentId ? { clientDocumentId } : {}),
    });
    return this.http.post<BatterySizingResponse>('api/batteries/sizing', body);
  }

  getAvailableEnclosures(
    projectId: string,
    enclosureFilters: EnclosureFilters,
  ): Observable<EnclosureProposition[]> {
    return this.http
      .post<AvailableEnclosuresResponse>(
        `${this.URL}/${projectId}/enclosure`,
        toStrapiRequestPayload(enclosureFilters),
      )
      .pipe(
        map((response) => EnclosureMapper.toEnclosurePropositions(response)),
      );
  }
}
